import { REGEXP_EMAIL, REGEXP_EMAILS, REGEXP_WEBSITE } from 'src/utils/pattern/pattern'

export const validateEmail = (email: string, allowMultiple = false) =>
  allowMultiple ? REGEXP_EMAILS.test(email) : REGEXP_EMAIL.test(email)

export const validatePassword = (password: string) => password.length >= 5

export const validateRepeatedPassword = (password: string, repeatedPassword: string) => password === repeatedPassword

export const validateName = (name: string) => name.trim().length >= 2

export const validateGroupName = (name: string) => name.trim().length >= 5

export const validateGroupDescription = (description: string) => description.trim().length >= 5

export const isNotEmpty = (value?: string): boolean =>
  !!(value && value.trim())

export const validateWebsite = (url: string): boolean => REGEXP_WEBSITE.test(url)

export const validateFlagMessage = (text: string): boolean => text.trim().length >= 5

/**
 * The backend types a meeting or event link as a Url scalar. A pasted
 * "meet.google.com/abc" has no scheme and is rejected there, which reaches the
 * user as nothing but the generic error alert - so it gets a scheme here.
 */
export const normaliseUrl = (value: string): string => {
  const trimmed = value.trim()

  if (!trimmed || /^https?:\/\//i.test(trimmed)) {
    return trimmed
  }

  return `https://${trimmed}`
}

// Labels of letters, digits and inner hyphens, joined by dots. `URL` hands back
// an already punycoded hostname, so this never has to see a non-ASCII character.
const HOSTNAME = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)*$/i

/**
 * The form the server takes: scheme in front, host punycoded, path encoded.
 *
 * `isUsableLink` accepts `münchen-treff.de/termin`, because `URL` hands back an
 * already punycoded host - but the raw string still carries the umlaut, and the
 * scalar answers "URI must be ascii only". So what is sent is what was parsed,
 * not what was typed.
 *
 * A value `URL` cannot parse comes back normalised but otherwise untouched, so
 * `not a url` returns `https://not a url` rather than the input. The submit
 * refuses such a value before it gets here; the preview builder does not, and
 * renders whatever it is handed.
 */
export const urlForServer = (value: string): string => {
  const normalised = normaliseUrl(value)

  try {
    return new URL(normalised).href
  } catch {
    return normalised
  }
}

/**
 * Whether we can hand this value to the server without it coming back as the
 * generic error.
 *
 * What the scalar rejects, measured against the staging API: `javascript:`,
 * `ftp://` and anything without a scheme all answer "is not a valid URL" before
 * the mutation runs. So everything this function lets through is http(s), which
 * is what makes it safe to render as an href later.
 *
 * Parsing is what does the work. `javascript:alert(1)` becomes
 * `https://javascript:alert(1)` on the way in, which `URL` refuses outright -
 * checking the text for a dot let `javascript:alert(1.0)` through, because the
 * version number carries one.
 *
 * The dot is only required from someone who did not type a scheme. A bare word
 * is a link not pasted yet, and saying so here is cheaper than an event nobody
 * can attend; `https://localhost:3000` is somebody who means it, and an intranet
 * meeting is a real one.
 */
export const isUsableLink = (value: string): boolean => {
  const trimmed = value.trim()

  // Whitespace anywhere is the scalar's rule, and `URL` would quietly encode it
  // into the path instead of refusing it.
  if (!trimmed || /\s/.test(trimmed)) {
    return false
  }

  // The scheme is checked before normalising, so that a value carrying one is
  // judged on it rather than on what prefixing would make of it. For `mailto:`
  // the credentials check below would catch it too (`https://mailto:a@b.de`
  // parses with `b.de` as host and `mailto` as username) - this is the earlier
  // of two lines, not the only one.
  //
  // No dot in the pattern, although RFC 3986 allows one in a scheme: with it,
  // the host of `bbb.firma.de:8443/b/abc` read as a scheme, and a host with a
  // port and no scheme is what an intranet meeting looks like.
  const scheme = trimmed.match(/^([a-z][a-z0-9+-]*):/i)

  if (scheme && !/^https?$/i.test(scheme[1])) {
    return false
  }

  // Two leading slashes, in either direction - a browser reads `\\evil.com` as
  // `//evil.com`. A single one passes and becomes `https://evil.com/`, which is
  // left alone on purpose: the host that ends up stored is the host the text
  // showed, so nothing is hidden by it.
  if (/^[/\\]{2}/.test(trimmed)) {
    return false
  }

  let url: URL

  try {
    url = new URL(normaliseUrl(trimmed))
  } catch {
    return false
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return false
  }

  // Everything before an `@` is credentials, not the host.
  // `meet.google.com@evil.com` reads as a Google link and goes to evil.com - and
  // the row that shows it truncates from the right, so the part that stays
  // visible is exactly the reassuring half. An empty pair (`https://@evil.com`)
  // passes this, and may: there is no misleading half to read.
  if (url.username || url.password) {
    return false
  }

  if (!HOSTNAME.test(url.hostname)) {
    return false
  }

  return /^https?:\/\//i.test(trimmed) || url.hostname.includes('.')
}
